import type { NodeTrace } from "../types";
import {
  GRAPH_LAYERS,
  foldNodeStates,
  unknownNodes,
  type NodeState,
} from "../lib/graphTopology";

interface Props {
  trace: NodeTrace[];
  thinking: boolean;
}

const TITLES: Record<string, string> = {
  ingest: "Records the turn. Agent turns skip the workflow nodes entirely.",
  classify_intent: "Local classifier first; the model only arbitrates the 0.45–0.75 band.",
  policy_gate: "Forces sensitive intents into verification until state says verified.",
  identity_verification: "Slot-fills the required identity fields across turns.",
  knowledge_qa: "Retrieves from the knowledge base and drafts a grounded answer.",
  claim_intake: "Fills the claim schema and reports what is still missing.",
  general_assist: "Smalltalk, escalation, unclear intent, and the locked case.",
  compose_guidance: "Turns state into the sidebar payload. Always runs.",
  update_summary: "Rolling summary, every few turns or on drift.",
};

function nodeClass(state: NodeState, ranThisTurn: boolean): string {
  if (state.status === "error") return "gnode error";
  if (state.status === "pending") return "gnode pending";
  if (state.status === "ok") return "gnode ok";
  // Once the turn has resolved, a node that never ran is a road not taken.
  return ranThisTurn ? "gnode skipped" : "gnode";
}

/**
 * The graph as the graph, rather than as a list of node names.
 *
 * The sidebar's other cards say what the Copilot concluded; this one says how it got
 * there. Because the backend streams a node event as each node completes, the path fills
 * in live - so the conditional routing that carries most of the design (the verification
 * gate diverting a claim request, and handing back to it in the same turn once verified)
 * is something the agent watches happen rather than something they infer afterwards.
 */
export function GraphView({ trace, thinking }: Props) {
  const states = foldNodeStates(trace, thinking);
  const ranThisTurn = trace.length > 0 && !thinking;
  const unknown = unknownNodes(trace);
  const total = trace.reduce((sum, entry) => sum + (entry.ms ?? 0), 0);

  return (
    <div className="graph">
      {GRAPH_LAYERS.map((layer, layerIndex) => (
        <div key={layerIndex} className="glayer-wrap">
          <div className={`glayer ${layer.length > 1 ? "branch" : ""}`}>
            {layer.map((node) => {
              const state = states[node.id];
              return (
                <div
                  key={node.id}
                  className={nodeClass(state, ranThisTurn)}
                  title={TITLES[node.id]}
                >
                  <span className="gname">{node.label}</span>
                  {state.status === "ok" || state.status === "error" ? (
                    <span className="gms">{Math.round(state.ms ?? 0)}ms</span>
                  ) : null}
                  {node.usesModel && <span className="gmodel" title="May call the model">◆</span>}
                </div>
              );
            })}
          </div>
          {layerIndex < GRAPH_LAYERS.length - 1 && (
            <div className={`gedge ${layer.length > 1 ? "fan-in" : ""}`} aria-hidden="true" />
          )}
        </div>
      ))}

      <div className="gfoot">
        {trace.length === 0 ? (
          <span>Idle. The graph runs when the customer sends a message.</span>
        ) : (
          <span>
            {thinking
              ? `Running. ${trace.length} node${trace.length === 1 ? "" : "s"} done so far.`
              : `${trace.length} node${trace.length === 1 ? "" : "s"} ran in ${Math.round(total)}ms.`}
          </span>
        )}
        {unknown.length > 0 && (
          <span className="warn">Not drawn: {unknown.join(", ")}</span>
        )}
      </div>
    </div>
  );
}
